import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, Mail, MessageCircle } from "lucide-react";
import { Header } from "@/components/Header";
import { SiteFooter } from "@/components/SiteFooter";
import { useLang } from "@/context/LangContext";
import { useSeo, SITE, ORG } from "@/lib/seo";
import { hubBySlug, HUBS } from "@/lib/seoHubs";
import { HUBS_I18N, UI, WHY } from "@/lib/seoHubsI18n";
import { GUIDES } from "@/lib/seoGuides";
import PAGES from "@/lib/seoPages.json";

const WA = "https://wa.me/41764411359";
const MAIL = "developers@graficawebdesign.com";
const LOCALE = { it: "it-CH", de: "de-CH", fr: "fr-CH" };
const pathFor = (lang, slug) => (lang === "it" ? `/${slug}` : `/${lang}/${slug}`);
const name = (h) => h.title.split(" | ")[0];

const localized = (slug, lang) => {
  const base = hubBySlug[slug];
  if (lang === "it") return base;
  const tr = HUBS_I18N[lang][slug];
  return { ...base, ...tr, sections: [...tr.sections, WHY[lang]] };
};

const jsonLd = (h, lang) => [
  { "@context": "https://schema.org", "@type": "Service", name: h.h1, serviceType: name(h), description: h.description, inLanguage: LOCALE[lang],
    provider: ORG, areaServed: [{ "@type": "AdministrativeArea", name: "Ticino" }, { "@type": "Country", name: "Svizzera" }],
    ...(h.price ? { offers: { "@type": "Offer", priceCurrency: "CHF", price: String(h.price),
      priceSpecification: { "@type": "UnitPriceSpecification", price: String(h.price), priceCurrency: "CHF", unitText: h.unit } } } : {}) },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: h.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: name(h), item: `${SITE}${pathFor(lang, h.slug)}` }] },
];

const Cta = ({ h, ui }) => (
  <div className="mt-8 flex flex-wrap gap-3">
    <Link to="/" data-testid="hub-cta-audit" className="h-12 px-6 inline-flex items-center gap-2 bg-[#E0262C] text-white font-medium hover:bg-[#2B3036] transition-colors">
      {ui.audit} <ArrowRight className="w-4 h-4" />
    </Link>
    <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="hub-cta-whatsapp" className="h-12 px-5 inline-flex items-center gap-2 border border-[#2B3036] hover:bg-white transition-colors">
      <MessageCircle className="w-4 h-4" /> WhatsApp
    </a>
    <a href={`mailto:${MAIL}?subject=${encodeURIComponent(name(h))}`} data-testid="hub-cta-email" className="h-12 px-5 inline-flex items-center gap-2 border border-[#2B3036] hover:bg-white transition-colors">
      <Mail className="w-4 h-4" /> {ui.tech}
    </a>
  </div>
);

const Section = ({ s }) => (
  <section className="py-8 border-t border-[#cfd6d9]">
    <h2 className="font-display text-2xl tracking-tight">{s.h2}</h2>
    <p className="mt-3 text-[15px] leading-relaxed text-[#3f454c] max-w-[68ch]">{s.p}</p>
    {s.list && <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2">{s.list.map((x) => <li key={x} className="flex gap-2 text-sm text-[#2B3036]"><Check className="w-4 h-4 mt-0.5 text-[#10b981] shrink-0" />{x}</li>)}</ul>}
  </section>
);

const Faq = ({ items, ui }) => (
  <section data-testid="hub-faq" className="py-8 border-t border-[#cfd6d9]">
    <h2 className="font-display text-2xl tracking-tight">{ui.faq}</h2>
    <div className="mt-4 divide-y divide-[#cfd6d9] border-y border-[#cfd6d9]">
      {items.map(([q, a]) => (
        <details key={q} className="group py-4">
          <summary className="cursor-pointer list-none flex justify-between gap-4 font-medium"><h3 className="text-base">{q}</h3><ChevronRight className="w-4 h-4 mt-1 transition-transform group-open:rotate-90" /></summary>
          <p className="mt-2 text-sm text-[#3f454c] leading-relaxed">{a}</p>
        </details>
      ))}
    </div>
  </section>
);

export const AuthorBio = ({ lang = "it" }) => {
  const ui = UI[lang];
  return (
    <div data-testid="author-bio" className="mt-10 border-l-2 border-[#E0262C] bg-white px-5 py-4 text-sm text-[#3f454c]">
      <strong className="text-[#09090b]">{ui.author}</strong> · PIXAWEB Sagl, Via Cantonale 19, 6900 Lugano. {ui.authorTail}{" "}
      <Link to="/sicurezza-e-privacy" className="underline hover:text-[#E0262C]">{ui.security}</Link>
    </div>
  );
};

const Chips = ({ title, items }) => items.length > 0 && (
  <>
    <h2 className="mt-6 first:mt-0 font-display text-2xl tracking-tight">{title}</h2>
    <ul className="mt-3 flex flex-wrap gap-2">{items.map(([to, label, tid]) => <li key={to}><Link to={to} data-testid={tid} className="text-sm border border-[#cfd6d9] bg-white px-3 py-1.5 hover:border-[#2B3036]">{label}</Link></li>)}</ul>
  </>
);

const Related = ({ h, lang, ui }) => {
  const cities = h.cityPrefix ? PAGES.filter((p) => p.group === "citta" && p.slug.startsWith(h.cityPrefix)) : [];
  return (
    <section className="py-8 border-t border-[#cfd6d9]">
      <Chips title={ui.cities} items={cities.map((c) => [`/servizi/${c.slug}`, c.title, `hub-city-${c.slug}`])} />
      <Chips title={ui.others} items={HUBS.filter((x) => x.slug !== h.slug).map((o) => [pathFor(lang, o.slug), name(localized(o.slug, lang)), `hub-link-${o.slug}`])} />
      <Chips title={ui.guides} items={GUIDES.filter((g) => g.hub === h.slug).map((g) => [`/guide/${g.slug}`, g.h1, `hub-guide-${g.slug}`])} />
    </section>
  );
};

const LangSwitch = ({ slug, lang }) => (
  <div data-testid="hub-lang-switch" className="flex gap-1 text-xs font-mono">
    {["it", "de", "fr"].map((l) => <Link key={l} to={pathFor(l, slug)} hrefLang={l} data-testid={`hub-lang-${l}`} className={`px-2 py-1 border ${l === lang ? "bg-[#09090b] text-white border-[#09090b]" : "border-[#cfd6d9] hover:border-[#2B3036]"}`}>{l.toUpperCase()}</Link>)}
  </div>
);

export default function HubPage({ slug, lang = "it" }) {
  const h = useMemo(() => localized(slug, lang), [slug, lang]);
  const ui = UI[lang];
  const { lang: uiLang, setLang, t } = useLang();
  const seo = useMemo(() => ({ title: h.title, description: h.description, path: pathFor(lang, slug), jsonld: jsonLd(h, lang), lang,
    alternates: { it: pathFor("it", slug), de: pathFor("de", slug), fr: pathFor("fr", slug), "x-default": pathFor("it", slug) } }), [h, lang, slug]);
  useSeo(seo);
  return (
    <div data-testid={`hub-page-${slug}`} lang={lang} className="min-h-screen bg-[#E6EAEC] flex flex-col">
      <Header lang={uiLang} setLang={setLang} t={t} />
      <main className="max-w-5xl mx-auto w-full px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <nav aria-label="breadcrumb" data-testid="hub-breadcrumb" className="flex items-center gap-1 text-xs text-[#71717a]">
            <Link to="/" className="hover:text-[#2B3036]">Home</Link><ChevronRight className="w-3 h-3" /><span className="text-[#2B3036]">{name(h)}</span>
          </nav>
          <LangSwitch slug={slug} lang={lang} />
        </div>
        <h1 data-testid="hub-title" className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-[20ch]">{h.h1}</h1>
        <p data-testid="hub-lead" className="mt-6 text-base lg:text-lg leading-relaxed text-[#2B3036] max-w-[64ch]">{h.lead}</p>
        <Cta h={h} ui={ui} />
        <div className="mt-12">{h.sections.map((s) => <Section key={s.h2} s={s} />)}</div>
        {h.package && <Link to={`/#package-${h.package}`} data-testid="hub-package-link" className="inline-flex items-center gap-2 text-sm font-medium underline hover:text-[#E0262C]">{ui.packages} <ArrowRight className="w-4 h-4" /></Link>}
        <Faq items={h.faq} ui={ui} />
        <Related h={h} lang={lang} ui={ui} />
        <AuthorBio lang={lang} />
      </main>
      <SiteFooter />
    </div>
  );
}
